import { type AnchorHTMLAttributes, type ButtonHTMLAttributes, type ReactNode } from 'react';

type Variant = 'accent' | 'member' | 'wa' | 'dark' | 'secondary' | 'ghost' | 'wood';
type Size = 'sm' | 'md' | 'lg';
type VarianteHeredada = 'primario' | 'secundario';

type PropsComunes = {
	/** Solo tiene efecto en `variant='wood'`: pinta el cartel en verde lima (destino activo/recomendado). */
	active?: boolean;
	/** Sombra sólida que se hunde al pulsar. Por defecto activa en `accent`/`member`/`wa`; nunca en `wood` (tiene su propia sombra fija). */
	chunky?: boolean;
	/** Ocupa todo el ancho disponible. */
	full?: boolean;
	/** Nodo o emoji a la izquierda de la etiqueta. */
	icon?: ReactNode;
	/** Tamaño. */
	size?: Size;
	/** Estilo visual. `wood` es el cartel de madera del signpost RPG. */
	variant?: Variant;
	/**
	 * Alias heredado (36 consumidores de `secundario` y 1 de `primario` antes de esta tarea).
	 * `primario` → `variant='accent'`, `secundario` → `variant='secondary'`. Si se reciben ambos
	 * props a la vez, gana `variant`.
	 */
	variante?: VarianteHeredada;
};

type PropsBoton = PropsComunes & { as?: 'button' } & ButtonHTMLAttributes<HTMLButtonElement>;
type PropsEnlace = PropsComunes & { as: 'a' } & AnchorHTMLAttributes<HTMLAnchorElement>;

export type ButtonProps = PropsBoton | PropsEnlace;

const MAPA_VARIANTE_HEREDADA: Record<VarianteHeredada, Variant> = {
	primario: 'accent',
	secundario: 'secondary',
};

const VARIANTES_CHUNKY_POR_DEFECTO = new Set<Variant>(['accent', 'member', 'wa']);

/**
 * H-04: el halo de foco ya no es el `ring` lima al 20% (`--focus-ring`) — sobre `variant='accent'`
 * (fondo lima) se fundía con el propio botón, indistinguible de "sin foco". `--focus-ring` queda
 * reservado a `Input`/`Textarea`/`Select` (su uso de origen); botones usan un `outline` sólido de
 * tinta (`--color-ink`, ≥ 3:1 tanto sobre lima como sobre blanco), con ancho y desplazamiento
 * explícitos — Tailwind 4 ya resuelve `outline-2`/`outline-offset-2` con estilo sólido por defecto
 * (`@property --tw-outline-style`), sin necesitar `outline-none` ni `outline-solid` aparte.
 *
 * T-2.2: `cursor-pointer`/`disabled:cursor-not-allowed`/`aria-disabled:cursor-not-allowed` van aquí
 * a mano, igual que ya declaran `botonCuadrado.tsx` y `arrowButton.tsx` — la capa base global de
 * `cursor: pointer` (`index.css`, `@layer base`) solo alcanza a `button`, `select` y `[role=button]`,
 * y `clasesDeBoton()` (ver más abajo) se exporta también para vestir de botón redondeado un
 * `<Link>` del router, que renderiza un `<a>` sin ninguno de esos tres selectores. Consumidores
 * reales de ese caso: `components/ui/estadoError.tsx`, `features/bienvenida/components/bienvenida.tsx`
 * y `features/invitations/components/listaEnlaces.tsx`.
 */
const BASE =
	'inline-flex min-h-11 items-center justify-center gap-2 whitespace-nowrap rounded font-body font-black no-underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none aria-disabled:pointer-events-none aria-disabled:opacity-50 aria-disabled:shadow-none cursor-pointer disabled:cursor-not-allowed aria-disabled:cursor-not-allowed';

const TAMANOS: Record<Size, string> = {
	lg: 'px-6 py-3.5 text-md',
	md: 'px-5 py-2.5 text-sm',
	sm: 'px-3.5 py-2 text-xs',
};

/**
 * H-20: `dark`/`member` escriben con `text-text-on-dark` (utilidad ya expuesta desde
 * `--text-on-dark` de `tokens.css`, `#ffffff` — el mismo blanco que `text-white` pintaba, medido
 * en Storybook antes/después: `rgb(255, 255, 255)` en ambos casos), no con `text-white` a mano.
 * Es la misma tinta que ya usan `Badge`/`BotonCuadrado`/`StatBlock` sobre fondo oscuro; antes este
 * componente era el único que reimplementaba el mismo blanco con la utilidad genérica de Tailwind
 * en vez del token del sistema.
 */
const ESTILOS_VARIANTE: Record<Exclude<Variant, 'wood'>, string> = {
	accent: 'border border-transparent bg-accent text-accent-ink hover:brightness-95',
	dark: 'border border-transparent bg-ink text-text-on-dark hover:brightness-90',
	ghost: 'border border-border bg-transparent text-gray-ink hover:bg-paper hover:text-ink',
	member: 'border border-transparent bg-member text-text-on-dark hover:brightness-95',
	secondary: 'border border-border bg-paper-2 text-ink hover:brightness-95',
	wa: 'border border-transparent bg-wa text-ink uppercase hover:brightness-95',
};

/**
 * Sombra y movimiento "chunky" por variante (H-08): mapa **estático**, con las tres clases de
 * cada estado (reposo/hover/active) escritas de forma literal, sin interpolación de plantilla.
 * Tailwind solo genera CSS para las clases que puede leer como texto literal al escanear el
 * código fuente — una cadena compuesta en tiempo de ejecución con `${...}` (como hacía la versión
 * anterior de este fichero, `sombraChunky()`) nunca llega a generarse. Por eso cada entrada de
 * abajo es una cadena ya completa, no una plantilla.
 *
 * `accent` usa la utilidad `shadow-press-accent` (`index.css`, `@theme inline`, ya expuesta desde
 * `--press-accent` de `tokens.css`: reposo a 4 px). `member`/`wa` usan sus propias utilidades
 * equivalentes, `shadow-press-member`/`shadow-press-wa` (reposo a 5 px). Ninguna de las tres tiene
 * una utilidad de tema para el hover/active (`tokens.css` solo declara el desplazamiento de
 * reposo), así que esos dos estados se escriben con sintaxis arbitraria de Tailwind
 * (`shadow-[0_Npx_0_color]`), repitiendo el mismo color que ya usa el token de reposo de esa
 * variante — `var(--da-accent-shadow)` para `accent` (variable propia, ya en `tokens.css`) y el
 * mismo literal `rgba` que `--press-member`/`--press-wa` incrustan para `member`/`wa` (no tienen
 * una variable propia con solo el color). El desplazamiento cambia siempre igual respecto al de
 * reposo — hover +2 px, active −3 px — sea cual sea el punto de partida (4 px en `accent` → 6/1;
 * 5 px en `member`/`wa` → 7/2), para que el "hundimiento" se sienta igual en las tres.
 *
 * `dark`/`ghost`/`secondary` no tienen sombra chunky propia (no aparecen en
 * `VARIANTES_CHUNKY_POR_DEFECTO` y ninguna pantalla fuerza `chunky` en ellas), pero conservan aquí
 * la misma entrada que `accent` — el mismo valor por defecto que ya usaba `sombraChunky()` — por si
 * algún consumidor futuro pasa `chunky` explícito sobre una de ellas.
 */
const SOMBRA_CHUNKY_ACCENT =
	'shadow-press-accent hover:shadow-[0_6px_0_var(--da-accent-shadow)] active:shadow-[0_1px_0_var(--da-accent-shadow)] motion-safe:transition-[transform,box-shadow] motion-safe:duration-fast motion-safe:ease-bounce hover:-translate-y-px active:translate-y-0.5';
const SOMBRA_CHUNKY_MEMBER =
	'shadow-press-member hover:shadow-[0_7px_0_rgba(180,0,170,0.3)] active:shadow-[0_2px_0_rgba(180,0,170,0.3)] motion-safe:transition-[transform,box-shadow] motion-safe:duration-fast motion-safe:ease-bounce hover:-translate-y-px active:translate-y-0.5';
const SOMBRA_CHUNKY_WA =
	'shadow-press-wa hover:shadow-[0_7px_0_rgba(20,140,60,0.3)] active:shadow-[0_2px_0_rgba(20,140,60,0.3)] motion-safe:transition-[transform,box-shadow] motion-safe:duration-fast motion-safe:ease-bounce hover:-translate-y-px active:translate-y-0.5';

const SOMBRA_Y_MOVIMIENTO_CHUNKY: Record<Exclude<Variant, 'wood'>, string> = {
	accent: SOMBRA_CHUNKY_ACCENT,
	dark: SOMBRA_CHUNKY_ACCENT,
	ghost: SOMBRA_CHUNKY_ACCENT,
	member: SOMBRA_CHUNKY_MEMBER,
	secondary: SOMBRA_CHUNKY_ACCENT,
	wa: SOMBRA_CHUNKY_WA,
};

const CLASE_WOOD_SIGN = 'from-sign-1 to-sign-2';
const CLASE_WOOD_ACTIVO = 'from-accent-deep to-accent';

function resolverVariante(variant: Variant | undefined, variante: VarianteHeredada | undefined): Variant {
	if (variant) {
		return variant;
	}
	if (variante) {
		return MAPA_VARIANTE_HEREDADA[variante];
	}
	return 'accent';
}

/**
 * Sombra y movimiento de la variante `wood`: cartel de madera con sombra permanente (token
 * `shadow-press-wood`, desplazamiento de reposo propio de 5 px), independiente del prop `chunky`
 * — igual que en el CSS de referencia del cliente (`Button.jsx`, `BTN_CSS`), donde `wood` queda
 * excluido de la clase `.is-chunky`. El estado `:active` reduce la sombra a `0 3px 0` (un único
 * plano de color, sin la sombra ambiental secundaria del CSS original: no hay token que la
 * represente y añadirla a mano rompería la regla de "solo tokens existentes").
 */
const CLASES_MADERA =
	'shadow-press-wood motion-safe:transition-[transform,box-shadow] motion-safe:duration-fast motion-safe:ease-bounce hover:-translate-y-px active:translate-y-0.5 active:shadow-[0_3px_0_var(--da-ink-warm)]';

/**
 * Sombra y movimiento del resto de variantes cuando `chunky` está activo: consulta el mapa
 * estático `SOMBRA_Y_MOVIMIENTO_CHUNKY` (ver JSDoc de ese mapa) en vez de componer la clase en
 * tiempo de ejecución — así Tailwind ve las tres clases de cada variante como texto literal al
 * escanear este fichero y las genera de verdad.
 */
function clasesSombraYMovimiento(variant: Variant, chunky: boolean | undefined): string {
	if (variant === 'wood') {
		return CLASES_MADERA;
	}
	const activa = chunky ?? VARIANTES_CHUNKY_POR_DEFECTO.has(variant);
	if (!activa) {
		return 'motion-safe:transition-transform motion-safe:duration-fast active:scale-[.97]';
	}
	return SOMBRA_Y_MOVIMIENTO_CHUNKY[variant];
}

type ArgsClasesDeBoton = {
	chunky?: boolean;
	full?: boolean;
	size?: Size;
	variant?: Variant;
};

/**
 * Clases del botón como cadena pura, sin renderizar nada — para que un `<Link>` del router pueda
 * vestirse de botón sin duplicar estilos ni importar el componente. No acepta `active`: esa marca
 * (recolorea `wood` a verde) es exclusiva del componente `Button`, ver JSDoc de más abajo.
 */
export const clasesDeBoton = ({
	chunky,
	full = false,
	size = 'md',
	variant = 'accent',
}: ArgsClasesDeBoton = {}): string =>
	[
		BASE,
		TAMANOS[size],
		variant === 'wood'
			? `border-[3px] border-ink-warm bg-gradient-to-b ${CLASE_WOOD_SIGN} text-ink-warm uppercase`
			: ESTILOS_VARIANTE[variant],
		clasesSombraYMovimiento(variant, chunky),
		full ? 'w-full' : '',
	]
		.filter(Boolean)
		.join(' ');

/**
 * Botón del sistema de diseño, portado desde `components/buttons/Button.d.ts` del design system
 * del cliente (CSS de referencia en el `Button.jsx` adjunto, constante `BTN_CSS`; uso en
 * `Button.prompt.md` y en `buttons.card.html`). Radio 12 px (`rounded`, token `--radius` — incluida
 * `wood`: el CSS del cliente le da 10 px propios, pero esta tarea unifica el radio del componente
 * en 12 px). Roboto en peso 900 (`font-body font-black`): el CSS del cliente usa `var(--font-body)`
 * para la familia (de ahí la elección entre `font-body`/`font-display`) pero pesa `var(--fw-bold)`
 * (700, no 900) — esta tarea pide 900 explícitamente, igual que ya cae `--font-display` a
 * "Roboto 900" en el resto de titulares de la app (ver `docs/design-system.md` § Fuentes). Altura
 * mínima 44 px en las tres variantes de tamaño (documento 05b §10.8): el CSS del cliente hace
 * botones `sm` más bajos que eso, pero el área táctil mínima de esta oleada no es negociable.
 *
 * Variante `ghost`: el CSS del cliente pinta su etiqueta en reposo con `--da-gray` (2,7:1 de
 * contraste sobre blanco — el propio `docs/design-system.md` § Contraste dice que ese token
 * "nunca vale para leer"). Se sustituye por `--da-gray-ink` (`text-gray-ink`, 5,1–5,7:1), la regla
 * de contraste de esta oleada gana sobre la fidelidad literal al CSS de referencia.
 *
 * Alias heredado: `variante: 'primario' | 'secundario'` sigue funcionando (ver `PropsComunes`).
 * `className` se sigue concatenando al final, como en la versión anterior de este componente.
 *
 * Exporta además `clasesDeBoton({ variant, size, chunky, full })`, ver JSDoc de esa función.
 */
export const Button = (props: ButtonProps): React.JSX.Element => {
	const {
		active = false,
		as,
		chunky,
		children,
		className = '',
		full = false,
		icon = null,
		size = 'md',
		variant,
		variante,
		...resto
	} = props;
	const varianteResuelta = resolverVariante(variant, variante);
	const clasesVariante = clasesDeBoton({ chunky, full, size, variant: varianteResuelta });
	const clases = `${
		varianteResuelta === 'wood' && active ? clasesVariante.replace(CLASE_WOOD_SIGN, CLASE_WOOD_ACTIVO) : clasesVariante
	} ${className}`.trim();

	const contenido = (
		<>
			{icon ? (
				<span aria-hidden='true' className='inline-flex items-center'>
					{icon}
				</span>
			) : null}
			{children}
		</>
	);

	if (as === 'a') {
		return (
			<a className={clases} {...(resto as AnchorHTMLAttributes<HTMLAnchorElement>)}>
				{contenido}
			</a>
		);
	}

	return (
		<button className={clases} type='button' {...(resto as ButtonHTMLAttributes<HTMLButtonElement>)}>
			{contenido}
		</button>
	);
};
